import type { ReactNode } from 'react';
import styles from './AssetTypeIcon.module.css';

type AssetTypeIconProps = {
  label: string;
  className?: string;
};

export function AssetTypeIcon({ label, className }: AssetTypeIconProps) {
  const kind = classifyAssetTypeGlyph(label);
  const wrapClass = [styles.icon, className].filter(Boolean).join(' ');
  return (
    <span className={wrapClass} aria-hidden>
      {iconForKind(kind)}
    </span>
  );
}

type GlyphKind =
  | 'all'
  | 'parking'
  | 'meeting'
  | 'gym'
  | 'workshop'
  | 'lab'
  | 'studio'
  | 'storage'
  | 'retail'
  | 'event'
  | 'kitchen'
  | 'rooftop'
  | 'medical'
  | 'lecture'
  | 'classroom'
  | 'coworking'
  | 'desk'
  | 'privateOffice'
  | 'open'
  | 'office'
  | 'generic';

/**
 * Ordered keyword matchers (English + Hebrew). More specific kinds come first so e.g.
 * "private office" wins over the generic "office" match.
 */
const GLYPH_MATCHERS: { kind: GlyphKind; test: RegExp }[] = [
  { kind: 'parking', test: /parking|car ?park|garage|חני/ },
  { kind: 'meeting', test: /meeting|conference|boardroom|ישיב|כנס/ },
  { kind: 'gym', test: /gym|fitness|workout|training|כושר/ },
  { kind: 'workshop', test: /workshop|maker|סדנ/ },
  { kind: 'lab', test: /lab\b|laborator|מעבד/ },
  { kind: 'studio', test: /studio|podcast|recording|סטודיו/ },
  { kind: 'storage', test: /storage|warehouse|locker|אחסון|מחסן/ },
  { kind: 'retail', test: /retail|shop|store|boutique|מסחר|חנות/ },
  { kind: 'event', test: /event|gala|party|exhibition|אירוע/ },
  { kind: 'kitchen', test: /kitchen|catering|culinary|מטבח/ },
  { kind: 'rooftop', test: /rooftop|terrace|garden|balcony|גג|גינ|מרפסת/ },
  { kind: 'medical', test: /clinic|medical|therapy|dental|מרפא|קליניק/ },
  { kind: 'lecture', test: /lecture ?room|lecture|seminar|אולם|הרצא/ },
  { kind: 'classroom', test: /classroom|כיתה/ },
  { kind: 'coworking', test: /business lounge|lounge|co-?working|shared|community|hub|עבודה משותפת|משותף|טרקלין/ },
  { kind: 'desk', test: /hot ?desk|dedicated ?desk|\bdesk|workstation|עמד/ },
  { kind: 'privateOffice', test: /private office|executive|dedicated office|משרד פרטי|מנהל/ },
  { kind: 'open', test: /open ?space|open ?plan|\bopen\b|פתוח/ },
  { kind: 'office', test: /office|suite|workspace|משרד/ },
];

function classifyAssetTypeGlyph(label: string): GlyphKind {
  const t = label.trim().toLowerCase();
  if (!t || t === 'all' || t.includes('all type')) {
    return 'all';
  }
  for (const matcher of GLYPH_MATCHERS) {
    if (matcher.test.test(t)) {
      return matcher.kind;
    }
  }
  return 'generic';
}

function iconForKind(kind: GlyphKind): ReactNode {
  switch (kind) {
    case 'all':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <rect x="14" y="14" width="7" height="7" rx="1.5" />
        </svg>
      );
    case 'parking':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="4" y="4" width="16" height="16" rx="3" />
          <path d="M9.5 8h3a2.4 2.4 0 0 1 0 4.8H9.5V8zm0 4.8V16" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'meeting':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <ellipse cx="12" cy="12" rx="8" ry="4.2" />
          <circle cx="4.5" cy="12" r="1.1" />
          <circle cx="19.5" cy="12" r="1.1" />
          <circle cx="12" cy="6.6" r="1.1" />
          <circle cx="12" cy="17.4" r="1.1" />
        </svg>
      );
    case 'open':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M3 21V9l9-5 9 5v12" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M3 13h18M9 21V13M15 21V13" />
        </svg>
      );
    case 'office':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M5 4h14v16H5z" />
          <path d="M9 8h6M9 12h6M9 16h4" strokeLinecap="round" />
        </svg>
      );
    case 'privateOffice':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M6 21V4a1 1 0 0 1 1-1h7l4 4v14" strokeLinejoin="round" />
          <path d="M14 3v4h4" strokeLinejoin="round" />
          <circle cx="11" cy="13" r="0.9" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'desk':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="5" y="4" width="14" height="9" rx="1.2" />
          <path d="M3 17h18M7 13v4M17 13v4M12 17v3M9 20h6" strokeLinecap="round" />
        </svg>
      );
    case 'coworking':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <circle cx="8" cy="8.5" r="2.3" />
          <circle cx="16" cy="8.5" r="2.3" />
          <path d="M3.5 18c0-2.4 2-4.2 4.5-4.2s4.5 1.8 4.5 4.2" strokeLinecap="round" />
          <path d="M12.5 17.6c.4-2 2.2-3.4 4.3-3.4 2.3 0 4.2 1.8 4.2 4.2" strokeLinecap="round" />
        </svg>
      );
    case 'gym':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M3 9v6M6 6.5v11M18 6.5v11M21 9v6M6 12h12" strokeLinecap="round" />
        </svg>
      );
    case 'workshop':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path
            d="M14.7 6.3a3.4 3.4 0 0 0-4.5 4.5L4 17l3 3 6.2-6.2a3.4 3.4 0 0 0 4.5-4.5l-2 2-2-2 1-2z"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'studio':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="3" y="7" width="13" height="10" rx="1.5" />
          <path d="M16 10.5l5-2.5v8l-5-2.5z" strokeLinejoin="round" />
        </svg>
      );
    case 'storage':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="3.5" y="4" width="7" height="7" rx="1" />
          <rect x="13.5" y="4" width="7" height="7" rx="1" />
          <rect x="8.5" y="13" width="7" height="7" rx="1" />
        </svg>
      );
    case 'retail':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M6 8h12l-1.2 12H7.2L6 8z" strokeLinejoin="round" />
          <path d="M9 8a3 3 0 0 1 6 0" strokeLinecap="round" />
        </svg>
      );
    case 'event':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6">
          <rect x="4" y="5" width="16" height="15" rx="2" />
          <path d="M4 9.5h16M8 3v4M16 3v4" strokeLinecap="round" />
          <path d="M12 12l.9 1.9 2.1.3-1.5 1.5.4 2.1-1.9-1-1.9 1 .4-2.1L9 14.2l2.1-.3.9-1.9z" strokeLinejoin="round" />
        </svg>
      );
    case 'kitchen':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M7 3v8M5 3v4a2 2 0 0 0 4 0V3M7 11v10" strokeLinecap="round" />
          <path d="M16 3c-1.4 0-2.3 2-2.3 4.6 0 2 1 3 2.3 3.2V21" strokeLinecap="round" />
        </svg>
      );
    case 'rooftop':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M12 21v-7" strokeLinecap="round" />
          <path
            d="M12 14c-3.2 0-5.5-2.2-5.5-5.4C6.5 5.3 9 2.8 12 2c3 .8 5.5 3.3 5.5 6.6 0 3.2-2.3 5.4-5.5 5.4z"
            strokeLinejoin="round"
          />
        </svg>
      );
    case 'medical':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M4 9v8" strokeLinecap="round" />
          <path d="M6 17h13" strokeLinecap="round" />
          <path d="M7 17V12.5l5.5-4" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M7 12.5h4.5" strokeLinecap="round" />
          <rect x="8.5" y="10.2" width="4.5" height="2.2" rx="0.9" />
          <path d="M9 17v2.5M17 17v2.5" strokeLinecap="round" />
        </svg>
      );
    case 'lab':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M9 3h6M10 3v6l-5 9.2A1.4 1.4 0 0 0 6.3 20h11.4a1.4 1.4 0 0 0 1.3-2.1L14 9V3" strokeLinejoin="round" />
          <path d="M7.5 15h9" strokeLinecap="round" />
        </svg>
      );
    case 'lecture':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="3" y="4" width="18" height="11" rx="1.5" />
          <path d="M7 19l5-4 5 4M12 4V2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      );
    case 'classroom':
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <rect x="4" y="4" width="16" height="11" rx="1.2" />
          <path d="M7 7.5h10M7 10h7.5M7 12.5h5" strokeLinecap="round" />
          <path d="M4 15h16" strokeLinecap="round" />
          <path d="M7 15v4.5M17 15v4.5" strokeLinecap="round" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
          <path d="M4 10l8-6 8 6v10H4V10z" strokeLinejoin="round" />
          <path d="M10 20v-5h4v5" strokeLinecap="round" />
        </svg>
      );
  }
}
